import React from 'react';
import { ReactComponent as WalletIcon } from 'assets/icons/icon-wallet.svg';
import {
  useGetBankAccountsQuery,
  GetBankAccountsQueryVariables,
} from './GetBankAccounts.generated';
import Page from 'components/Page';
import Stack from 'toolbox/src/components/Stack/Stack';
import Card from 'components/Card/Card';
import Table from 'components/Table/Table';
import { curr } from 'helpers/curr';
import Flex from 'toolbox/src/components/Flex/Flex';
import Button from 'components/Button';
import { useNavigate } from 'routeMap';
import { usePagination, useTextInput, useSortedTable } from 'toolbox';
import JcapPagination from 'components/JcapPagination/JcapPagination';
import SearchInput from 'components/SearchInput';

interface AdminViewBankAccountsProps {}

export const AdminViewBankAccounts: React.FC<AdminViewBankAccountsProps> = () => {
  const navigate = useNavigate();
  const pagination = usePagination({
    resultsPerPage: 25,
  });
  const searchInput = useTextInput();
  const { onColumnClick, sortingState, orderBy } = useSortedTable<
    GetBankAccountsQueryVariables['orderBy']
  >({
    defaultOrderBy: 'ACCOUNT ID',
    mapping: {
      'ACCOUNT ID': (o) => ({
        account_id: o,
      }),
      Organisation: (o) => ({
        currency_book: {
          business_unit: {
            organisation: {
              name: o,
            },
          },
        },
      }),
      'Currency Book': (o) => ({
        currency_book: {
          name: o,
        },
      }),
      Bank: (o) => ({
        bank: {
          name: o,
        },
      }),
      Type: (o) => ({
        product_type: o,
      }),
      'Interest Rate': (o) => ({
        opening_interest_rate: o,
      }),
    },
  });
  const [result] = useGetBankAccountsQuery({
    variables: {
      limit: pagination.limit,
      offset: pagination.offset,
      search: `%${searchInput.throttledValue}%`,
      orderBy,
    },
  });

  pagination.useWatchAllResultsCount(
    result.data?.bank_accounts_aggregate?.aggregate?.count,
  );

  if (!result.data) {
    return null;
  }

  const bankAccounts = result?.data?.bank_accounts;

  return (
    <Page
      icon={
        <WalletIcon className="inline w-10 h-10 mr-3 -mb-2 text-gray-500" />
      }
      title="Bank Accounts"
      cardWrapperClassName="max-w-full"
    >
      <Card>
        <Stack>
          <Flex justifyContent="space-between" alignItems="center">
            <div className="max-w-xs -mb-2">
              <SearchInput
                {...searchInput.inputProps}
                placeholder="Search..."
              />
            </div>
            <Button onClick={() => navigate.createBankAccount()}>
              Add New Bank Account
            </Button>
          </Flex>
          <Table
            sortingState={sortingState}
            onColumnClick={onColumnClick}
            data={bankAccounts || []}
            gridTemplateColumns={
              '1.4fr 1.4fr repeat(2, 1fr) 0.6fr repeat(2, 1fr) 50px'
            }
            columns={[
              {
                Header: 'ACCOUNT ID',
                accessor: ({ account_id }) => account_id,
              },
              {
                Header: 'Organisation',
                accessor: (data) => (
                  <div>
                    {data.currency_book.business_unit.organisation.name}
                    <br />
                    {data.currency_book.business_unit.name}
                  </div>
                ),
              },
              {
                Header: 'Currency Book',
                accessor: ({ currency_book }) => currency_book?.name,
              },
              {
                Header: 'Bank',
                accessor: ({ bank }) => bank?.name,
              },
              {
                Header: 'Type',
                accessor: (data) =>
                  data.product_type === 'NOTICE' ? (
                    <div>
                      NOTICE
                      <br />
                      {data.notice_days}D
                    </div>
                  ) : data.product_type === 'FIXED' ? (
                    <div>
                      FIXED
                      <br />
                      {data.fixed_maturity_date}
                    </div>
                  ) : (
                    <div>{data.product_type}</div>
                  ),
              },
              {
                Header: 'Interest Rate',
                accessor: (data) => data.opening_interest_rate + '%',
              },
              {
                Header: 'Latest Balance',
                accessor: (data) =>
                  data?.actual_balances[0]
                    ? data.currency.symbol +
                      ' ' +
                      curr(data.actual_balances[0].balance)
                    : '-',
              },
              {
                id: 'actions',
                accessor: ({ account_id }) => (
                  <button
                    onClick={() =>
                      navigate.viewBankAccountById({
                        params: {
                          account_id: account_id,
                        },
                      })
                    }
                    className="p-1 px-2 rounded bg-jcap-100 text-jcap-700"
                  >
                    View
                  </button>
                ),
              },
            ]}
          />
          <JcapPagination {...pagination} />
        </Stack>
      </Card>
    </Page>
  );
};

export default AdminViewBankAccounts;
